import { createFileRoute } from '@tanstack/react-router';
import { Badge } from '@cartcloser/ui';

export const Route = createFileRoute('/admin/$slug/whats-new')({
  component: WhatsNewPage,
});

type ChangeKind = 'New' | 'Improved' | 'Fixed';

interface Change {
  kind: ChangeKind;
  title: string;
  detail: string;
}

interface ReleaseMonth {
  /** Display label, e.g. "July 2026". */
  month: string;
  changes: Change[];
}

/**
 * Customer-facing release notes, newest month first. Written for merchants,
 * not engineers — describe the shopper-facing benefit, not the implementation.
 */
const RELEASES: ReleaseMonth[] = [
  {
    month: 'July 2026',
    changes: [
      {
        kind: 'Improved',
        title: 'Quicker template editing for platform admins',
        detail:
          'Platform administrators can now jump straight from a linked agent to its underlying template. A lock icon makes platform-only controls easy to recognise throughout the dashboard.',
      },
      {
        kind: 'Improved',
        title: 'Private owner access for launch',
        detail:
          'The dashboard can now be restricted to an approved owner account, with a private setup code used for first-time access instead of public self-service signup.',
      },
      {
        kind: 'Improved',
        title: 'Honest alternatives when nothing fits',
        detail:
          'When a shopper’s must-haves leave no matching products, your expert now says so instead of quietly breaking a requirement. It explains what loosening each constraint would unlock and asks the shopper which trade-off they prefer.',
      },
      {
        kind: 'New',
        title: 'Price freshness on every recommendation',
        detail:
          'Product specs rarely change, but prices do. Every recommendation card now shows when its price was last imported, so shoppers know to check the latest price before buying.',
      },
      {
        kind: 'Improved',
        title: 'Experts qualify only when it helps',
        detail:
          'Your agents ask a focused question when a missing answer could change the ranking, but can recommend immediately when a shopper already gives enough detail. Picks appear as tidy cards with evidence and the most relevant trade-off for each option.',
      },
      {
        kind: 'Improved',
        title: 'Honest about must-haves',
        detail:
          'When a shopper says a feature is essential, your agent only recommends products that genuinely have it — and if your product data can’t confirm it, the agent says so plainly instead of guessing. The sandbox shows which must-haves it could match against your specs.',
      },
      {
        kind: 'New',
        title: 'See the model and cost behind each conversation',
        detail:
          'The Conversations view now records which AI model handled each chat, and marks conversations that ran on your local models as free — handy for comparing models and understanding costs.',
      },
      {
        kind: 'New',
        title: 'Inline help across the dashboard',
        detail:
          'Look for the “?” next to settings like SKU scope and persona — tap it for a plain-English explanation of what each option does and when to use it.',
      },
      {
        kind: 'Improved',
        title: 'Tidier navigation',
        detail:
          'The sidebar now groups related pages under “Agents” and “Products” so it’s easier to find your way around as your store grows.',
      },
      {
        kind: 'Improved',
        title: 'Sharper recommendations for specific requests',
        detail:
          'When a shopper spells out a requirement — “under 60cm wide”, “below 45 decibels” — your expert now narrows to the products that genuinely fit before recommending, so the shortlist matches what they actually asked for.',
      },
      {
        kind: 'Improved',
        title: 'More accurate routing to the right expert',
        detail:
          'Shoppers are connected to the best-matched expert more reliably, so conversations start on the right foot and reach a confident recommendation sooner.',
      },
      {
        kind: 'Improved',
        title: 'Faster, more focused long conversations',
        detail:
          'Extended chats stay quick and stay anchored on what the shopper said most recently, keeping replies relevant deep into a conversation.',
      },
      {
        kind: 'Fixed',
        title: 'Catalogue imports count your range correctly',
        detail:
          'Re-importing products you already have no longer counts them twice against your plan, so routine feed updates go through cleanly.',
      },
      {
        kind: 'Fixed',
        title: 'Smoother agent editing',
        detail:
          'Switching straight from one expert to another now always shows that expert’s own settings, with no leftover values from the previous screen.',
      },
    ],
  },
  {
    month: 'June 2026',
    changes: [
      {
        kind: 'New',
        title: 'Expert agents',
        detail:
          'Build a team of product experts, each focused on a category like “Dishwashers” or “Mesh wi-fi”. A lightweight greeter listens to each shopper and hands them to the right expert to guide them to a confident choice.',
      },
      {
        kind: 'New',
        title: 'Product catalogue import',
        detail:
          'Bring your whole range in from a CSV or JSON feed. Organise products into categories and keep them up to date by re-importing whenever your range changes.',
      },
      {
        kind: 'New',
        title: 'Automatic equivalent-product grouping',
        detail:
          'CartCloser recognises when several listings are effectively the same product and groups them, so if one is out of stock your assistant can offer an in-stock equivalent instead of a dead end.',
      },
      {
        kind: 'New',
        title: 'Answers drawn from your own documents',
        detail:
          'Upload manuals and spec sheets and CartCloser turns them into facts your experts can use, so shoppers get accurate, on-brand answers grounded in your own material.',
      },
      {
        kind: 'New',
        title: 'Sandbox for private testing',
        detail:
          'Chat with your experts privately to see exactly how they respond and what they recommend, so you can refine them before a single shopper sees them.',
      },
      {
        kind: 'New',
        title: 'Conversation insights',
        detail:
          'Review every shopper conversation, see which recommendations were shown and clicked, and understand where conversations are turning into conversions.',
      },
      {
        kind: 'New',
        title: 'One-line storefront assistant',
        detail:
          'Add the assistant to your storefront by pasting a single snippet. It opens in a tidy chat window and starts helping shoppers straight away.',
      },
    ],
  },
];

const KIND_VARIANT: Record<ChangeKind, 'default' | 'success' | 'secondary'> = {
  New: 'default',
  Improved: 'success',
  Fixed: 'secondary',
};

function WhatsNewPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">What’s new</h1>
        <p className="text-sm text-muted-foreground">
          The latest improvements to CartCloser, newest first.
        </p>
      </div>

      {RELEASES.map((release) => (
        <section key={release.month} className="space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold tracking-tight">{release.month}</h2>
            <div className="h-px flex-1 bg-border" />
          </div>

          <ul className="space-y-4">
            {release.changes.map((change) => (
              <li key={change.title} className="rounded-lg border border-border bg-card p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={KIND_VARIANT[change.kind]}>{change.kind}</Badge>
                  <h3 className="text-sm font-medium">{change.title}</h3>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{change.detail}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
